import RenewalDraftInvoices from '../components/RenewalDraftInvoices';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { trackFeatureUsage, trackNavigation } from '../utils/analytics';
import { formatDateForDisplay } from '../utils/dateFormat';
import DailyLogDashboardModal from '../components/DailyLogDashboardModal';
import DailyLogMonitoringModal from '../components/DailyLogMonitoringModal';

const createEmptyQuickAddReading = () => ({
  readingDate: new Date().toISOString().split('T')[0],
  weight: '',
  totalFatPercentage: '',
  visceralFat: '',
  bmr: '',
  bmi: '',
  metaAge: '',
  tsf: '',
  skeletalMusclePercentage: ''
});

const PRODUCT_PRICE_SLABS = [
  { value: 'MRP', label: 'MRP', priceField: 'mrp' },
  { value: 'BRONZE', label: 'Bronze', priceField: 'bronzePrice' },
  { value: 'SILVER', label: 'Silver', priceField: 'silverPrice' },
  { value: 'GOLD', label: 'Gold', priceField: 'goldPrice' },
  { value: 'SUCCESS_BUILDER', label: 'Success Builder', priceField: 'successBuilderPrice' },
  { value: 'SUPERVISOR', label: 'Supervisor', priceField: 'associatePrice' }
];

const formatEstimateAmount = (amount) => new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2
}).format(Number(amount) || 0);

const getEstimateUnitPrice = (product, slabCode) => {
  const slab = PRODUCT_PRICE_SLABS.find((option) => option.value === slabCode);
  if (!slab) return null;

  const price = Number(product[slab.priceField]);
  return Number.isFinite(price) ? price : null;
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const isEditor = user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR');
  const isAdmin = user?.systemRoles?.some(role => role.code === 'SYSTEM_ADMIN' || role.code === 'SUPER_ADMIN');
  const [stats, setStats] = useState({
    totalDownline: 0,
    directMembers: 0,
    activeMembers: 0
  });
  const [loading, setLoading] = useState(true);
  const [quickCountsLoading, setQuickCountsLoading] = useState(true);
  const [todaysCheckupCount, setTodaysCheckupCount] = useState(null);
  const [todaysBirthdayCount, setTodaysBirthdayCount] = useState(null);
  const [todaysFollowupsCount, setTodaysFollowupsCount] = useState(null);
  const [todaysCustomerFollowupsCount, setTodaysCustomerFollowupsCount] = useState(null);
  const [todaysLeadFollowupsCount, setTodaysLeadFollowupsCount] = useState(null);
  const [upcomingFollowupsCount, setUpcomingFollowupsCount] = useState(null);
  const [upcomingCustomerFollowupsCount, setUpcomingCustomerFollowupsCount] = useState(null);
  const [upcomingLeadFollowupsCount, setUpcomingLeadFollowupsCount] = useState(null);
  const [renewalDueCount, setRenewalDueCount] = useState(null);
  const [duePaymentsCount, setDuePaymentsCount] = useState(null);
  const [draftInvoicesCount, setDraftInvoicesCount] = useState(null);
  const [showDraftInvoices, setShowDraftInvoices] = useState(false);
  const [dueSettlementsCount, setDueSettlementsCount] = useState(null);
  const [activeCustomersCount, setActiveCustomersCount] = useState(null);
  const [inactiveCustomersCount, setInactiveCustomersCount] = useState(null);
  const [totalCustomersCount, setTotalCustomersCount] = useState(null);
  const [affiliatedCustomersCount, setAffiliatedCustomersCount] = useState(null);
  const [dailyLogMonitoring, setDailyLogMonitoring] = useState(null);
  const [dailyLogMonitoringLoading, setDailyLogMonitoringLoading] = useState(false);
  const [dailyLogMonitoringError, setDailyLogMonitoringError] = useState('');
  const [showDailyLogMonitoring, setShowDailyLogMonitoring] = useState(false);
  const [selectedDailyLogCustomer, setSelectedDailyLogCustomer] = useState(null);
  const [showQuickAction, setShowQuickAction] = useState(null);
  const [quickActionData, setQuickActionData] = useState([]);
  const [upcomingFollowupData, setUpcomingFollowupData] = useState({ leads: [], customers: [] });
  const [upcomingFollowupTab, setUpcomingFollowupTab] = useState('lead');
  const [selectedFollowupCustomer, setSelectedFollowupCustomer] = useState(null);
  const [followupHistory, setFollowupHistory] = useState([]);
  const [followupHistoryLoading, setFollowupHistoryLoading] = useState(false);
  const [followupHistoryError, setFollowupHistoryError] = useState('');
  const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);
  const [leadDetailsFollowups, setLeadDetailsFollowups] = useState([]);
  const [leadDetailsLoading, setLeadDetailsLoading] = useState(false);
  const [leadDetailsError, setLeadDetailsError] = useState('');
  const [showLeadFollowupForm, setShowLeadFollowupForm] = useState(false);
  const [leadFollowupForm, setLeadFollowupForm] = useState({ description: '', nextFollowupAt: '' });
  const [leadFollowupSaving, setLeadFollowupSaving] = useState(false);
  const [leadFollowupFormError, setLeadFollowupFormError] = useState('');
  const [leadActionLoading, setLeadActionLoading] = useState(false);
  const [quickActionLoading, setQuickActionLoading] = useState(false);
  const [quickActionError, setQuickActionError] = useState('');
  const [sortField, setSortField] = useState('dueDate');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(10);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [quickAddSearch, setQuickAddSearch] = useState('');
  const [quickAddResults, setQuickAddResults] = useState([]);
  const [quickAddSearchLoading, setQuickAddSearchLoading] = useState(false);
  const [quickAddSearchError, setQuickAddSearchError] = useState('');
  const [selectedQuickAddCustomer, setSelectedQuickAddCustomer] = useState(null);
  const [quickAddReading, setQuickAddReading] = useState(createEmptyQuickAddReading());
  const [quickAddSubmitting, setQuickAddSubmitting] = useState(false);
  const [quickAddSubmitError, setQuickAddSubmitError] = useState('');
  const [quickAddSuccessMessage, setQuickAddSuccessMessage] = useState('');
  const [showProductEstimateModal, setShowProductEstimateModal] = useState(false);
  const [estimateProducts, setEstimateProducts] = useState([]);
  const [estimateProductsLoading, setEstimateProductsLoading] = useState(false);
  const [estimateProductsError, setEstimateProductsError] = useState('');
  const [estimateSearch, setEstimateSearch] = useState('');
  const [estimateItems, setEstimateItems] = useState([]);
  const [estimatePriceSlab, setEstimatePriceSlab] = useState('');

  useEffect(() => {
    fetchDashboardStats();
    fetchQuickActionCounts();
    // Track dashboard view
    trackFeatureUsage('Admin Dashboard', 'view');
  }, []);

  useEffect(() => {
    if (!showQuickAddModal) {
      return undefined;
    }

    const trimmedSearch = quickAddSearch.trim();
    if (trimmedSearch.length < 2) {
      setQuickAddResults([]);
      setQuickAddSearchLoading(false);
      setQuickAddSearchError('');
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      setQuickAddSearchLoading(true);
      setQuickAddSearchError('');

      try {
        const response = await axios.get('/api/customers/quick-add-options', {
          params: { search: trimmedSearch }
        });
        setQuickAddResults(response.data || []);
      } catch (error) {
        console.error('Failed to search quick-add customers:', error);
        setQuickAddResults([]);
        setQuickAddSearchError('Unable to search customers right now. Please try again.');
      } finally {
        setQuickAddSearchLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [quickAddSearch, showQuickAddModal]);

  const fetchDashboardStats = async () => {
    try {
      console.log('Fetching dashboard stats from:', '/api/dashboard/stats');
      const response = await axios.get('/api/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
      console.error('Error details:', error.response?.status, error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuickActionCounts = async () => {
    setQuickCountsLoading(true);
    try {
      const response = await axios.get('/api/customers/dashboard-card-counts');
      const counts = response.data || {};

      setTodaysCheckupCount(counts.todaysCheckup ?? 0);
      setTodaysBirthdayCount(counts.todaysBirthday ?? 0);
      const customerFollowups = counts.todaysCustomerFollowups ?? counts.todaysFollowups ?? 0;
      const leadFollowups = counts.todaysLeadFollowups ?? 0;
      setTodaysCustomerFollowupsCount(customerFollowups);
      setTodaysLeadFollowupsCount(leadFollowups);
      setTodaysFollowupsCount(counts.todaysFollowups ?? (customerFollowups + leadFollowups));
      const upcomingCustomers = counts.upcomingCustomerFollowups ?? 0;
      const upcomingLeads = counts.upcomingLeadFollowups ?? 0;
      setUpcomingCustomerFollowupsCount(upcomingCustomers);
      setUpcomingLeadFollowupsCount(upcomingLeads);
      setUpcomingFollowupsCount(counts.upcomingFollowups ?? (upcomingCustomers + upcomingLeads));
      setRenewalDueCount(counts.renewalDue ?? 0);
      setDuePaymentsCount(counts.duePayments ?? 0);
      try { setDraftInvoicesCount((await axios.get('/api/renewal-drafts')).data.length); }
      catch (error) { console.error('Unable to load draft invoice count', error); setDraftInvoicesCount(null); }
      try {
        const settlementsResponse = await axios.get('/api/product-settlements/due/count');
        setDueSettlementsCount(settlementsResponse.data?.count ?? 0);
      } catch (settlementsError) {
        console.error('Failed to fetch due product settlement count:', settlementsError);
        setDueSettlementsCount(0);
      }
      setActiveCustomersCount(counts.activeCustomers ?? 0);
      setInactiveCustomersCount(counts.inactiveCustomers ?? 0);
      setTotalCustomersCount(counts.totalCustomers ?? 0);
      setAffiliatedCustomersCount(counts.totalAffiliatedCustomers ?? 0);
      if (isEditor) await fetchDailyLogMonitoring();
    } catch (error) {
      console.error('Failed to fetch quick action counts:', error);
    } finally {
      setQuickCountsLoading(false);
    }
  };

  const fetchDailyLogMonitoring = async () => {
    setDailyLogMonitoringLoading(true);
    setDailyLogMonitoringError('');
    try {
      const response = await axios.get('/api/daily-logs/monitoring/today');
      setDailyLogMonitoring(response.data);
    } catch (requestError) {
      console.error('Failed to load daily log monitoring:', requestError);
      setDailyLogMonitoringError(t('Unable to load daily log monitoring.'));
    } finally {
      setDailyLogMonitoringLoading(false);
    }
  };

  const openDailyLogMonitoring = () => {
    setShowDailyLogMonitoring(true);
    fetchDailyLogMonitoring();
  };

  const fetchQuickActionData = async (actionType, options = {}) => {
    setQuickActionLoading(true);
    setQuickActionError('');
    
    try {
      let response;
      const nextPage = options.page ?? currentPage;
      const nextSortField = options.sortField ?? sortField;
      const nextSortDirection = options.sortDirection ?? sortDirection;

      switch (actionType) {
        case 'checkup':
          response = await axios.get('/api/customers/todays-checkup');
          setQuickActionData(response.data);
          setTotalPages(0);
          setTotalElements(response.data.length);
          break;
        case 'birthday':
          response = await axios.get('/api/customers/todays-birthday');
          setQuickActionData(response.data);
          setTotalPages(0);
          setTotalElements(response.data.length);
          break;
        case 'followups':
          response = await axios.get('/api/customers/followups/today');
          setQuickActionData(response.data || []);
          setTotalPages(0);
          setTotalElements((response.data || []).length);
          break;
        case 'lead-followups':
          response = await axios.get('/api/leads/followups/today');
          setQuickActionData(response.data || []);
          setTotalPages(0);
          setTotalElements((response.data || []).length);
          break;
        case 'upcoming-followups': {
          const [leadResponse, customerResponse] = await Promise.all([
            axios.get('/api/leads/followups/upcoming'),
            axios.get('/api/customers/followups/upcoming')
          ]);
          const leads = leadResponse.data || [];
          const customers = customerResponse.data || [];
          setUpcomingFollowupData({ leads, customers });
          setUpcomingFollowupTab('lead');
          setQuickActionData(leads);
          setTotalPages(0);
          setTotalElements(leads.length + customers.length);
          break;
        }
        case 'renewal':
          response = await axios.get('/api/customers/renewal-due-paged', {
            params: {
              page: nextPage,
              size: pageSize,
              sortBy: nextSortField,
              sortDir: nextSortDirection
            }
          });
          setQuickActionData(response.data.content);
          setTotalPages(response.data.totalPages);
          setTotalElements(response.data.totalElements);
          break;
        case 'payments':
          const params = {
            page: nextPage,
            size: pageSize,
            sortBy: nextSortField,
            sortDir: nextSortDirection
          };
          response = await axios.get('/api/customers/due-payments', { params });
          setQuickActionData(response.data.content);
          setTotalPages(response.data.totalPages);
          setTotalElements(response.data.totalElements);
          break;
        case 'settlements':
          response = await axios.get('/api/product-settlements/due', {
            params: { page: nextPage, size: pageSize, sortBy: nextSortField, sortDir: nextSortDirection }
          });
          setQuickActionData(response.data.content);
          setTotalPages(response.data.totalPages);
          setTotalElements(response.data.totalElements);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Failed to fetch ${actionType} data:`, error);
      setQuickActionError(`Failed to load ${actionType} data. Please try again.`);
    } finally {
      setQuickActionLoading(false);
    }
  };

  const handleQuickAction = (actionType) => {
    const defaultSortField = actionType === 'renewal' ? 'programEndDate' : 'dueDate';

    setShowQuickAction(actionType);
    setSortField(defaultSortField);
    setSortDirection('asc');
    setCurrentPage(0);
    fetchQuickActionData(actionType, {
      page: 0,
      sortField: defaultSortField,
      sortDirection: 'asc'
    });
  };

  const closeQuickActionModal = () => {
    setShowQuickAction(null);
    setQuickActionData([]);
    setUpcomingFollowupData({ leads: [], customers: [] });
    setUpcomingFollowupTab('lead');
    setQuickActionError('');
    setSelectedFollowupCustomer(null);
    setFollowupHistory([]);
    setFollowupHistoryError('');
    setSelectedLeadDetails(null);
    setLeadDetailsFollowups([]);
    setLeadDetailsError('');
  };

  const openFollowupHistory = async (followup) => {
    setSelectedFollowupCustomer(followup);
    setFollowupHistory([]);
    setFollowupHistoryError('');
    setFollowupHistoryLoading(true);

    try {
      const response = await axios.get(`/api/customers/${followup.customerId}/followups`);
      setFollowupHistory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch followup history:', error);
      setFollowupHistoryError('Unable to load followup history. Please try again.');
    } finally {
      setFollowupHistoryLoading(false);
    }
  };

  const openLeadDetails = async (followup) => {
    setSelectedLeadDetails({
      id: followup.leadId,
      fullName: followup.leadName,
      email: followup.leadEmail,
      phone: followup.leadPhone
    });
    setLeadDetailsFollowups([]);
    setLeadDetailsError('');
    setLeadDetailsLoading(true);

    const [detailsResult, followupsResult] = await Promise.allSettled([
      axios.get(`/api/leads/${followup.leadId}`),
      axios.get(`/api/leads/${followup.leadId}/followups`)
    ]);

    if (detailsResult.status === 'fulfilled') {
      setSelectedLeadDetails(detailsResult.value.data);
    } else {
      console.error('Failed to fetch lead details:', detailsResult.reason);
      setLeadDetailsError('Unable to load complete lead details. Please try again.');
    }

    if (followupsResult.status === 'fulfilled') {
      setLeadDetailsFollowups(followupsResult.value.data || []);
    } else {
      console.error('Failed to fetch lead followup history:', followupsResult.reason);
      setLeadDetailsError('Unable to load complete lead details and followup history. Please try again.');
    }

    setLeadDetailsLoading(false);
  };

  const openLeadFollowupForm = () => {
    const nextHour = new Date();
    nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);
    const localDateTime = new Date(nextHour.getTime() - nextHour.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);

    setLeadFollowupForm({ description: '', nextFollowupAt: localDateTime });
    setLeadFollowupFormError('');
    setShowLeadFollowupForm(true);
  };

  const submitLeadFollowup = async (event) => {
    event.preventDefault();
    if (!selectedLeadDetails?.id) return;

    setLeadFollowupSaving(true);
    setLeadFollowupFormError('');
    try {
      const response = await axios.post(`/api/leads/${selectedLeadDetails.id}/followups`, leadFollowupForm);
      const savedFollowup = response.data;
      setLeadDetailsFollowups((current) => [savedFollowup, ...current]);
      setShowLeadFollowupForm(false);
      setLeadFollowupForm({ description: '', nextFollowupAt: '' });

      if (savedFollowup.nextFollowupAt && new Date(savedFollowup.nextFollowupAt).toDateString() === new Date().toDateString()) {
        setQuickActionData((current) => [...current, savedFollowup]
          .sort((left, right) => new Date(left.nextFollowupAt) - new Date(right.nextFollowupAt)));
      }
      fetchQuickActionCounts();
    } catch (error) {
      console.error('Failed to add lead followup:', error);
      setLeadFollowupFormError(error.response?.data?.message || 'Failed to save followup. Please try again.');
    } finally {
      setLeadFollowupSaving(false);
    }
  };

  const updateLeadFromDashboard = async (action) => {
    if (!selectedLeadDetails?.id) return;

    setLeadActionLoading(true);
    setLeadDetailsError('');
    try {
      const response = action === 'convert'
        ? await axios.post(`/api/leads/${selectedLeadDetails.id}/convert`)
        : await axios.put(`/api/leads/${selectedLeadDetails.id}/status?status=CLOSED`);
      setSelectedLeadDetails(response.data);
    } catch (error) {
      console.error(`Failed to ${action} lead:`, error);
      setLeadDetailsError(error.response?.data?.message || `Failed to ${action} lead. Please try again.`);
    } finally {
      setLeadActionLoading(false);
    }
  };

  const openQuickAddModal = () => {
    closeQuickActionModal();
    setQuickAddSuccessMessage('');
    setQuickAddSearch('');
    setQuickAddResults([]);
    setQuickAddSearchError('');
    setSelectedQuickAddCustomer(null);
    setQuickAddReading(createEmptyQuickAddReading());
    setQuickAddSubmitError('');
    setShowQuickAddModal(true);
  };

  const closeQuickAddModal = () => {
    setShowQuickAddModal(false);
    setQuickAddSuccessMessage('');
    setQuickAddSearch('');
    setQuickAddResults([]);
    setQuickAddSearchLoading(false);
    setQuickAddSearchError('');
    setSelectedQuickAddCustomer(null);
    setQuickAddReading(createEmptyQuickAddReading());
    setQuickAddSubmitting(false);
    setQuickAddSubmitError('');
  };

  const openProductEstimateModal = async () => {
    setShowProductEstimateModal(true);
    setEstimateProducts([]);
    setEstimateProductsError('');
    setEstimateProductsLoading(true);
    setEstimateSearch('');
    setEstimateItems([]);
    setEstimatePriceSlab('');
    trackFeatureUsage('Product Estimate', 'open');

    try {
      const response = await axios.get('/api/products/all');
      setEstimateProducts(response.data || []);
    } catch (error) {
      console.error('Failed to load products for estimate:', error);
      setEstimateProductsError('Unable to load products right now. Please try again.');
    } finally {
      setEstimateProductsLoading(false);
    }
  };

  const closeProductEstimateModal = () => {
    setShowProductEstimateModal(false);
    setEstimateProducts([]);
    setEstimateProductsError('');
    setEstimateProductsLoading(false);
    setEstimateSearch('');
    setEstimateItems([]);
    setEstimatePriceSlab('');
  };

  const addEstimateProduct = (product) => {
    setEstimateItems((currentItems) => {
      const existingItem = currentItems.find((item) => item.product.id === product.id);
      if (existingItem) {
        return currentItems.map((item) => item.product.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item);
      }

      return [...currentItems, { product, quantity: 1 }];
    });
    setEstimateSearch('');
  };

  const updateEstimateQuantity = (productId, quantity) => {
    const nextQuantity = Math.max(1, Math.min(999, Math.floor(Number(quantity) || 1)));
    setEstimateItems((currentItems) => currentItems.map((item) => item.product.id === productId
      ? { ...item, quantity: nextQuantity }
      : item));
  };

  const removeEstimateProduct = (productId) => {
    setEstimateItems((currentItems) => currentItems.filter((item) => item.product.id !== productId));
  };

  const handleQuickAddReadingChange = (field, value) => {
    setQuickAddReading(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const submitQuickAddReading = async (saveMode) => {
    if (!selectedQuickAddCustomer?.id) {
      setQuickAddSubmitError('Please select a customer before saving the health reading.');
      return;
    }

    setQuickAddSubmitting(true);
    setQuickAddSubmitError('');
    setQuickAddSuccessMessage('');

    try {
      const payload = {
        readingDate: quickAddReading.readingDate,
        weight: quickAddReading.weight ? parseFloat(quickAddReading.weight) : null,
        totalFatPercentage: quickAddReading.totalFatPercentage ? parseFloat(quickAddReading.totalFatPercentage) : null,
        visceralFat: quickAddReading.visceralFat ? parseFloat(quickAddReading.visceralFat) : null,
        bmr: quickAddReading.bmr ? parseFloat(quickAddReading.bmr) : null,
        bmi: quickAddReading.bmi ? parseFloat(quickAddReading.bmi) : null,
        metaAge: quickAddReading.metaAge ? parseInt(quickAddReading.metaAge, 10) : null,
        tsf: quickAddReading.tsf ? parseFloat(quickAddReading.tsf) : null,
        skeletalMusclePercentage: quickAddReading.skeletalMusclePercentage ? parseFloat(quickAddReading.skeletalMusclePercentage) : null
      };

      await axios.post(`/api/customers/${selectedQuickAddCustomer.id}/health-readings`, payload);

      setQuickAddSuccessMessage('Record added successfully.');
      fetchQuickActionCounts();

      if (saveMode === 'addMore') {
        setQuickAddSearch('');
        setQuickAddResults([]);
        setQuickAddSearchError('');
        setSelectedQuickAddCustomer(null);
        setQuickAddReading(createEmptyQuickAddReading());
        setQuickAddSubmitError('');
      } else {
        closeQuickAddModal();
      }
    } catch (error) {
      console.error('Failed to save quick-add health reading:', error);
      setQuickAddSubmitError(error.response?.data || 'Failed to save health reading. Please try again.');
    } finally {
      setQuickAddSubmitting(false);
    }
  };

  const handleQuickAddSubmit = async (event) => {
    event.preventDefault();
    await submitQuickAddReading('close');
  };

  const handleSort = (field) => {
    const nextSortDirection = sortField === field
      ? (sortDirection === 'asc' ? 'desc' : 'asc')
      : (field === 'dueAmount' ? 'desc' : 'asc');

    if (sortField === field) {
      setSortDirection(nextSortDirection);
    } else {
      setSortField(field);
      setSortDirection(nextSortDirection);
    }
    setCurrentPage(0);
    if (showQuickAction === 'payments' || showQuickAction === 'renewal' || showQuickAction === 'settlements') {
      fetchQuickActionData(showQuickAction, {
        page: 0,
        sortField: field,
        sortDirection: nextSortDirection
      });
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (showQuickAction === 'payments' || showQuickAction === 'renewal' || showQuickAction === 'settlements') {
      fetchQuickActionData(showQuickAction, { page: newPage });
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'var(--success)';
      case 'INACTIVE': return 'var(--warning)';
      case 'BLOCKED': return 'var(--error)';
      default: return 'var(--gray-dark)';
    }
  };

  const getInvoiceStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'var(--success)';
      case 'PENDING': return 'var(--warning)';
      case 'OVERDUE': return 'var(--error)';
      case 'CANCELLED': return 'var(--gray-dark)';
      default: return 'var(--gray-dark)';
    }
  };

  const getQuickActionTitle = (actionType) => {
    switch (actionType) {
      case 'checkup': return t("Today's Checkup");
      case 'birthday': return t("Today's Birthday");
      case 'renewal': return t('Renewal Due (Next 7 Days)');
      case 'payments': return t('Due Payments');
      case 'settlements': return t('Due Product Settlements');
      case 'followups': return t("Today's Followups");
      case 'lead-followups': return t("Today's Lead Followups");
      case 'upcoming-followups': return t('Upcoming Followups (Next 7 Days)');
      default: return '';
    }
  };

  const getQuickActionIcon = (actionType) => {
    switch (actionType) {
      case 'checkup': return "📋";
      case 'birthday': return "🎂";
      case 'renewal': return "🔄";
      case 'payments': return "💰";
      case 'settlements': return "📦";
      case 'followups': return "☎️";
      case 'lead-followups': return "☎️";
      case 'upcoming-followups': return "📅";
      default: return "📊";
    }
  };

  const renderCount = (value) => (
    quickCountsLoading || value === null || value === undefined ? '...' : value
  );


  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <h1 className="page-title">Welcome, {user.fullName || user.email}!</h1>

      {showDraftInvoices && <RenewalDraftInvoices onClose={() => setShowDraftInvoices(false)} onChanged={() => axios.get('/api/renewal-drafts').then(r => setDraftInvoicesCount(r.data.length))} />}
      {quickAddSuccessMessage && (
        <div className="alert alert-success" style={{ marginBottom: '20px' }}>
          {quickAddSuccessMessage}
        </div>
      )}
      
      <div className="card mb-20">
        <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
          Your Profile
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div>
            <strong>Email:</strong> {user.email}
          </div>
          <div>
            <strong>Member Role:</strong> {user.memberRole?.displayName || 'Not Assigned'}
          </div>
          <div>
            <strong>System Roles:</strong> {
              user.systemRoles?.length > 0 
                ? user.systemRoles.map(role => role.displayName).join(', ')
                : 'None'
            }
          </div>
          <div>
            <strong>Status:</strong> 
            <span style={{ 
              color: user.status === 'ACTIVE' ? 'var(--success)' : 'var(--warning)',
              marginLeft: '5px'
            }}>
              {user.status}
            </span>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        {!isAdmin && (
          <div
            className="stat-card"
            onClick={() => window.location.href = '/customers'}
            style={{ cursor: 'pointer', transition: 'transform 0.2s ease' }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            <div className="stat-number">{renderCount(totalCustomersCount)}</div>
            <div className="stat-label">{t('My Customers')}</div>
            <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
              <span
                style={{ cursor: 'pointer', textDecoration: 'underline', color: 'var(--success)' }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = '/customers?status=ACTIVE';
                }}
              >
                {t('Active')}: {renderCount(activeCustomersCount)}
              </span>
              {' | '}
              <span
                style={{ cursor: 'pointer', textDecoration: 'underline', color: 'var(--warning)' }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = '/customers?status=INACTIVE';
                }}
              >
                {t('Inactive')}: {renderCount(inactiveCustomersCount)}
              </span>
              {' | '}
              <span
                style={{ cursor: 'pointer', textDecoration: 'underline', color: 'var(--error)' }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = '/customers?status=ARCHIVED';
                }}
              >
                {t('Archived')}: {renderCount(
                  totalCustomersCount !== null && activeCustomersCount !== null && inactiveCustomersCount !== null
                    ? totalCustomersCount - activeCustomersCount - inactiveCustomersCount
                    : null
                )}
              </span>
            </div>
          </div>
        )}

        <div 
          className="stat-card"
          onClick={() => window.location.href = '/affiliated-customers'}
          style={{ cursor: 'pointer', transition: 'transform 0.2s ease' }}
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
        >
          <div className="stat-number">{renderCount(affiliatedCustomersCount)}</div>
          <div className="stat-label">{t('Affiliated Customers')}</div>
          <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
            {t('From affiliated clubs')}
          </div>
        </div>
        
        {!isEditor && (
          <div 
            className="stat-card"
            onClick={() => window.location.href = '/members'}
            style={{ cursor: 'pointer', transition: 'transform 0.2s ease' }}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
        >
          <div className="stat-number">{stats.directMembers}</div>
          <div className="stat-label">{t('First Line Supervisors')}</div>
        </div>
      )}
        
        <div 
          className="stat-card"
          onClick={() => window.location.href = '/members'}
          style={{ cursor: 'pointer', transition: 'transform 0.2s ease', order: 98 }}
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
        >
          <div className="stat-number">{stats.totalDownline}</div>
          <div className="stat-label">{t('All Line Supervisors')}</div>
        </div>

        <div 
          className="stat-card"
          onClick={() => window.location.href = '/hierarchy'}
          style={{ cursor: 'pointer', transition: 'transform 0.2s ease', order: 99 }}
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
        >
          <div className="stat-number">{stats.totalDownline}</div>
          <div className="stat-label">{t('Org Tree View')}</div>
        </div>

        {isEditor && (
          <>
            <div
              className="stat-card"
              onClick={openDailyLogMonitoring}
              style={{ cursor: 'pointer', transition: 'transform 0.2s ease', background: 'linear-gradient(135deg, var(--white) 0%, #f4fff8 100%)', border: '2px solid var(--success)' }}
              onMouseEnter={(event) => event.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(event) => event.currentTarget.style.transform = 'scale(1)'}
            >
              <div className="stat-number">{dailyLogMonitoringLoading ? '...' : `${dailyLogMonitoring?.filledCount ?? 0}/${dailyLogMonitoring?.totalActiveCustomers ?? 0}`}</div>
              <div className="stat-label">{t('Daily Log Monitoring')}</div>
              <button type="button" onClick={(event) => { event.stopPropagation(); openDailyLogMonitoring(); }} style={{ border: 0, padding: 0, marginTop: '7px', background: 'transparent', color: 'var(--primary-blue)', font: 'inherit', fontSize: '12px', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>
                {t("Yesterday's log submitted")}: {dailyLogMonitoring?.filledCount ?? 0}
              </button>
            </div>

            <div
              className="stat-card"
              onClick={() => window.location.href = '/attendance-marker'}
              style={{ cursor: 'pointer', transition: 'transform 0.2s ease', background: 'linear-gradient(135deg, var(--white) 0%, var(--pale-blue) 100%)', border: '2px solid var(--primary-blue)' }}
              onMouseEnter={(event) => event.currentTarget.style.transform = 'scale(1.02)'}
              onMouseLeave={(event) => event.currentTarget.style.transform = 'scale(1)'}
            >
              <div className="stat-number" style={{ fontSize: '48px' }}>📱</div>
              <div className="stat-label" style={{ fontWeight: 'bold', color: 'var(--dark-blue)' }}>{t('QR Attendance Marker')}</div>
              <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>{t('Generate QR for attendance')}</div>
            </div>

            <div 
              className="stat-card" 
              onClick={() => handleQuickAction('checkup')}
              style={{ cursor: 'pointer', transition: 'transform 0.2s ease' }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              <div className="stat-number">{renderCount(todaysCheckupCount)}</div>
              <div className="stat-label">{t("Today's Checkup")}</div>
            </div>

            <div 
              className="stat-card" 
              onClick={() => handleQuickAction('birthday')}
              style={{ cursor: 'pointer', transition: 'transform 0.2s ease' }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              <div className="stat-number">{renderCount(todaysBirthdayCount)}</div>
              <div className="stat-label">{t("Today's Birthday")}</div>
            </div>

            <div
              className="stat-card"
              onClick={() => handleQuickAction('followups')}
              style={{ cursor: 'pointer', transition: 'transform 0.2s ease' }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              <div className="stat-number">{renderCount(todaysFollowupsCount)}</div>
              <div className="stat-label">{t("Today's Followups")}</div>
              <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
                <span
                  style={{ cursor: 'pointer', textDecoration: 'underline', color: 'var(--primary-blue)' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleQuickAction('followups');
                  }}
                >
                  {t('Customer Followup')}: {renderCount(todaysCustomerFollowupsCount)}
                </span>
                {' | '}
                <span
                  style={{ cursor: 'pointer', textDecoration: 'underline', color: 'var(--success)' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleQuickAction('lead-followups');
                  }}
                >
                  {t('Lead Followup')}: {renderCount(todaysLeadFollowupsCount)}
                </span>
              </div>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleQuickAction('upcoming-followups');
                }}
                style={{
                  border: 0,
                  padding: 0,
                  marginTop: '8px',
                  background: 'transparent',
                  color: 'var(--primary-blue)',
                  font: 'inherit',
                  fontSize: '12px',
                  fontWeight: 700,
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                {t('Upcoming Followups')}: {renderCount(upcomingFollowupsCount)}
              </button>
            </div>

            <div 
              className="stat-card" 
              onClick={() => handleQuickAction('renewal')}
              style={{ cursor: 'pointer', transition: 'transform 0.2s ease' }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              <div className="stat-number">{renderCount(renewalDueCount)}</div>
              <div className="stat-label">{t('Renewal Due')}</div>
            </div>

            <div 
              className="stat-card"
              style={{ transition: 'transform 0.2s ease' }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              <div className="stat-number" style={{ fontSize: '34px' }}>💰 | 📦</div>
              <div className="stat-label" style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <button type="button" onClick={() => handleQuickAction('payments')} style={{ border: 0, padding: 0, background: 'transparent', color: 'var(--primary-blue)', font: 'inherit', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>
                  {t('Due Payments')}: {renderCount(duePaymentsCount)}
                </button>
                <span>|</span>
                <button type="button" onClick={() => handleQuickAction('settlements')} style={{ border: 0, padding: 0, background: 'transparent', color: 'var(--primary-blue)', font: 'inherit', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer' }}>
                  {t('Due Settlements')}: {renderCount(dueSettlementsCount)}
                </button>
                <span>|</span><button type="button" onClick={() => setShowDraftInvoices(true)} style={{border:0,background:'transparent',color:'var(--primary-blue)',font:'inherit',fontWeight:700,textDecoration:'underline',cursor:'pointer'}}>Draft Invoices: {renderCount(draftInvoicesCount)}</button>
              </div>
            </div>

            <div 
              className="stat-card" 
              onClick={() => window.location.href = '/inventory'}
              style={{ 
                cursor: 'pointer', 
                transition: 'transform 0.2s ease',
                background: 'linear-gradient(135deg, var(--white) 0%, #f4fff8 100%)',
                border: '2px solid var(--success)'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              <div className="stat-number" style={{ fontSize: '48px' }}>📦</div>
              <div className="stat-label" style={{ fontWeight: 'bold', color: 'var(--dark-blue)' }}>{t('Inventory Management')}</div>
              <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
                {t('Upload E-invoices and track stock')}
              </div>
            </div>

            <div 
              className="stat-card" 
              onClick={() => window.location.href = '/reports'}
              style={{ cursor: 'pointer', transition: 'transform 0.2s ease' }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              <div className="stat-number">📊</div>
              <div className="stat-label">{t('Reports')}</div>
            </div>

            <div 
              className="stat-card" 
              onClick={() => window.location.href = '/leaderboard'}
              style={{ cursor: 'pointer', transition: 'transform 0.2s ease' }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              <div className="stat-number">🏆</div>
              <div className="stat-label">{t('Leaderboard')}</div>
            </div>

          </>
        )}
      </div>

      {showDailyLogMonitoring && !selectedDailyLogCustomer && (
        <DailyLogDashboardModal
          data={dailyLogMonitoring}
          loading={dailyLogMonitoringLoading}
          error={dailyLogMonitoringError}
          onClose={() => setShowDailyLogMonitoring(false)}
          onView={customer => setSelectedDailyLogCustomer({ id: customer.customerId, fullName: customer.customerName })}
          t={t}
        />
      )}
      {selectedDailyLogCustomer && (
        <DailyLogMonitoringModal
          customer={selectedDailyLogCustomer}
          initialStartDate={dailyLogMonitoring?.logDate}
          initialEndDate={dailyLogMonitoring?.logDate}
          onClose={() => setSelectedDailyLogCustomer(null)}
        />
      )}

      {(user.systemRoles?.some(role => role.code === 'SYSTEM_APPROVER') || 
        isAdmin || isEditor) && (
        <div className="card">
          <h3 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
            Administrative Actions
          </h3>
          <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
            {user.systemRoles?.some(role => role.code === 'SYSTEM_APPROVER') && (
              <a href="/pending-requests" className="btn btn-secondary">
                Pending Requests
              </a>
            )}
            {isAdmin && (
              <>
                <a href="/access-management" className="btn btn-secondary">
                  Access Management
                </a>
                <a href="/documents" className="btn btn-secondary">
                  Document Management
                </a>
                <a href="/products" className="btn btn-secondary">
                  Manage Products
                </a>
                <a href="/meal-plan-templates" className="btn btn-secondary">
                  Meal Plan Templates
                </a>
                <a href="/training-videos" className="btn btn-secondary">
                  Training Videos
                </a>
                <a href="/contests" className="btn btn-secondary">
                  Challenge Management
                </a>
              </>
            )}
            {isEditor && (
              <>
                <a href="/badges" className="btn btn-secondary">
                  Badge Management
                </a>
                <a href="/bulk-upload" className="btn btn-secondary">
                  Bulk Upload
                </a>
                <button type="button" className="btn btn-secondary" onClick={openProductEstimateModal}>
                  Product Estimate
                </button>
                <a href="/contests" className="btn btn-secondary">
                  Challenges
                </a>
              </>
            )}
            {user.systemRoles?.some(role => role.code === 'SUPER_ADMIN') && (
              <>
                <a href="/admin/subscription-invoices" className="btn btn-secondary">
                  Subscription Invoices
                </a>
              </>
            )}
          </div>
        </div>
      )}

      {showProductEstimateModal && (() => {
        const normalizedSearch = estimateSearch.trim().toLowerCase();
        const selectedProductIds = new Set(estimateItems.map((item) => item.product.id));
        const searchResults = normalizedSearch
          ? estimateProducts
            .filter((product) => !selectedProductIds.has(product.id))
            .filter((product) => [product.productName, product.stockNo]
              .some((value) => String(value || '').toLowerCase().includes(normalizedSearch)))
            .slice(0, 8)
          : [];
        const estimateTotal = estimateItems.reduce((total, item) => {
          const unitPrice = getEstimateUnitPrice(item.product, estimatePriceSlab);
          return total + (unitPrice === null ? 0 : unitPrice * item.quantity);
        }, 0);

        return (
          <div className="modal-overlay product-estimate-overlay">
            <div className="modal-content product-estimate-modal" role="dialog" aria-modal="true" aria-labelledby="productEstimateTitle">
              <div className="modal-header">
                <div>
                  <h3 id="productEstimateTitle">Product Estimate</h3>
                  <div className="product-estimate-subtitle">Prepare a quick price estimate for an order</div>
                </div>
                <button type="button" className="close-btn" onClick={closeProductEstimateModal} aria-label="Close product estimate">
                  ×
                </button>
              </div>

              <div className="product-estimate-body">
                {estimateProductsError && <div className="alert alert-error">{estimateProductsError}</div>}

                <section className="product-estimate-section">
                  <label htmlFor="productEstimateSearch" className="form-label">Add products</label>
                  <div className="product-estimate-search-wrap">
                    <input
                      id="productEstimateSearch"
                      type="search"
                      className="form-input"
                      value={estimateSearch}
                      onChange={(event) => setEstimateSearch(event.target.value)}
                      placeholder="Search by product name or stock number"
                      autoComplete="off"
                      disabled={estimateProductsLoading}
                    />

                    {normalizedSearch && !estimateProductsLoading && (
                      <div className="product-estimate-results" role="listbox" aria-label="Product search results">
                        {searchResults.length > 0 ? searchResults.map((product) => (
                          <button
                            key={product.id}
                            type="button"
                            className="product-estimate-result"
                            onClick={() => addEstimateProduct(product)}
                          >
                            <span>
                              <strong>{product.productName}</strong>
                              <small>Stock no. {product.stockNo}</small>
                            </span>
                            <span className="product-estimate-add-label">Add</span>
                          </button>
                        )) : (
                          <div className="product-estimate-empty-result">No matching products found.</div>
                        )}
                      </div>
                    )}
                  </div>
                  {estimateProductsLoading && <div className="product-estimate-loading">Loading products...</div>}
                </section>

                <section className="product-estimate-section">
                  <div className="product-estimate-section-heading">
                    <h4>Estimate items</h4>
                    <span>{estimateItems.length} {estimateItems.length === 1 ? 'item' : 'items'}</span>
                  </div>

                  {estimateItems.length === 0 ? (
                    <div className="product-estimate-empty">Search and add products to begin the estimate.</div>
                  ) : (
                    <div className="product-estimate-lines">
                      <div className="product-estimate-line product-estimate-line-header" aria-hidden="true">
                        <span>Product</span>
                        <span>Quantity</span>
                        <span>Amount</span>
                        <span />
                      </div>
                      {estimateItems.map((item) => {
                        const unitPrice = getEstimateUnitPrice(item.product, estimatePriceSlab);
                        return (
                          <div className="product-estimate-line" key={item.product.id}>
                            <div className="product-estimate-product">
                              <strong>{item.product.productName}</strong>
                              <span>{item.product.stockNo}</span>
                              {estimatePriceSlab && unitPrice !== null && <small>{formatEstimateAmount(unitPrice)} each</small>}
                            </div>
                            <div className="product-estimate-quantity" aria-label={`Quantity for ${item.product.productName}`}>
                              <button type="button" onClick={() => updateEstimateQuantity(item.product.id, item.quantity - 1)} disabled={item.quantity <= 1} aria-label="Decrease quantity">−</button>
                              <input
                                type="number"
                                min="1"
                                max="999"
                                value={item.quantity}
                                onChange={(event) => updateEstimateQuantity(item.product.id, event.target.value)}
                                aria-label={`Quantity for ${item.product.productName}`}
                              />
                              <button type="button" onClick={() => updateEstimateQuantity(item.product.id, item.quantity + 1)} disabled={item.quantity >= 999} aria-label="Increase quantity">+</button>
                            </div>
                            <strong className="product-estimate-line-total">
                              {estimatePriceSlab && unitPrice !== null ? formatEstimateAmount(unitPrice * item.quantity) : '—'}
                            </strong>
                            <button
                              type="button"
                              className="product-estimate-remove"
                              onClick={() => removeEstimateProduct(item.product.id)}
                              aria-label={`Remove ${item.product.productName}`}
                              title="Remove product"
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>

                {estimateItems.length > 0 && (
                  <section className="product-estimate-summary">
                    <div className="product-estimate-slab">
                      <label htmlFor="productEstimatePriceSlab" className="form-label">Price slab</label>
                      <select
                        id="productEstimatePriceSlab"
                        className="form-input"
                        value={estimatePriceSlab}
                        onChange={(event) => setEstimatePriceSlab(event.target.value)}
                      >
                        <option value="">Select price slab</option>
                        {PRODUCT_PRICE_SLABS.map((slab) => <option key={slab.value} value={slab.value}>{slab.label}</option>)}
                      </select>
                    </div>
                    <div className="product-estimate-total">
                      <span>Estimated total</span>
                      <strong>{estimatePriceSlab ? formatEstimateAmount(estimateTotal) : 'Select a price slab'}</strong>
                    </div>
                  </section>
                )}
              </div>

              <div className="product-estimate-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setEstimateItems([]);
                    setEstimatePriceSlab('');
                    setEstimateSearch('');
                  }}
                  disabled={estimateItems.length === 0}
                >
                  Clear Estimate
                </button>
                <button type="button" className="btn btn-primary" onClick={closeProductEstimateModal}>Done</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Quick Action Modal */}
      {showQuickAction && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '95%',
            maxWidth: showQuickAction === 'renewal' ? '1180px' : '1000px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
                {getQuickActionIcon(showQuickAction)} {getQuickActionTitle(showQuickAction)}
              </h3>
              <button
                onClick={closeQuickActionModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--gray-dark)'
                }}
              >
                ×
              </button>
            </div>

            {showQuickAction === 'checkup' && isEditor && (
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: '18px'
              }}>
                <button
                  className="btn btn-primary"
                  onClick={openQuickAddModal}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    borderRadius: '999px',
                    padding: '10px 18px',
                    boxShadow: '0 12px 24px rgba(40, 116, 240, 0.18)'
                  }}
                >
                  <span style={{ fontSize: '16px', lineHeight: 1 }}>+</span>
                  Quick Add
                </button>
              </div>
            )}

            {quickActionError && (
              <div className="alert alert-error">{quickActionError}</div>
            )}

            {quickActionLoading ? (
              <div className="loading">Loading data...</div>
            ) : (
              <div>
                {showQuickAction === 'upcoming-followups' && (
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid var(--gray-medium)' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setUpcomingFollowupTab('lead');
                        setQuickActionData(upcomingFollowupData.leads);
                      }}
                      style={{
                        border: 0,
                        borderBottom: upcomingFollowupTab === 'lead' ? '3px solid var(--primary-blue)' : '3px solid transparent',
                        background: 'transparent',
                        padding: '10px 14px',
                        color: upcomingFollowupTab === 'lead' ? 'var(--primary-blue)' : 'var(--gray-dark)',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      {t('Lead Followup')} ({upcomingLeadFollowupsCount ?? upcomingFollowupData.leads.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setUpcomingFollowupTab('customer');
                        setQuickActionData(upcomingFollowupData.customers);
                      }}
                      style={{
                        border: 0,
                        borderBottom: upcomingFollowupTab === 'customer' ? '3px solid var(--primary-blue)' : '3px solid transparent',
                        background: 'transparent',
                        padding: '10px 14px',
                        color: upcomingFollowupTab === 'customer' ? 'var(--primary-blue)' : 'var(--gray-dark)',
                        fontWeight: 700,
                        cursor: 'pointer'
                      }}
                    >
                      {t('Customer Followup')} ({upcomingCustomerFollowupsCount ?? upcomingFollowupData.customers.length})
                    </button>
                  </div>
                )}

                {showQuickAction === 'lead-followups' || (showQuickAction === 'upcoming-followups' && upcomingFollowupTab === 'lead') ? (
                  <div>
                    {quickActionData.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                        {showQuickAction === 'upcoming-followups'
                          ? 'No lead followups scheduled in the next 7 days.'
                          : 'No lead followups scheduled for today.'}
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '14px'
                        }}>
                          <thead>
                            <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Lead</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Contact</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Date</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Time</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Description / Comment</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Coach</th>
                              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quickActionData.map((followup, index) => (
                              <tr
                                key={followup.id}
                                style={{
                                  backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                                  borderBottom: '1px solid var(--gray-medium)'
                                }}
                              >
                                <td style={{ padding: '12px', fontWeight: '600' }}>
                                  {followup.leadName || '-'}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  <div>{followup.leadEmail || '-'}</div>
                                  <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '3px' }}>
                                    {followup.leadPhone || '-'}
                                  </div>
                                </td>
                                <td style={{ padding: '12px' }}>
                                  {formatDateForDisplay(followup.nextFollowupAt)}
                                </td>
                                <td style={{ padding: '12px', whiteSpace: 'nowrap' }}>
                                  {followup.nextFollowupAt
                                    ? new Date(followup.nextFollowupAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                                    : '-'}
                                </td>
                                <td style={{ padding: '12px', maxWidth: '420px', whiteSpace: 'pre-wrap' }}>
                                  {followup.description || '-'}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  {followup.coachName || '-'}
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                    onClick={() => openLeadDetails(followup)}
                                  >
                                    Details
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : showQuickAction === 'followups' || (showQuickAction === 'upcoming-followups' && upcomingFollowupTab === 'customer') ? (
                  <div>
                    {quickActionData.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                        {showQuickAction === 'upcoming-followups'
                          ? 'No customer followups scheduled in the next 7 days.'
                          : 'No followups scheduled for today.'}
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '14px'
                        }}>
                          <thead>
                            <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Customer</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Customer Type</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Date</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Time</th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>Latest Summary</th>
                              <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quickActionData.map((followup, index) => (
                              <tr
                                key={followup.id}
                                style={{
                                  backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                                  borderBottom: '1px solid var(--gray-medium)'
                                }}
                              >
                                <td style={{ padding: '12px', fontWeight: '600' }}>
                                  {followup.customerName}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  {followup.customerType ? t(followup.customerType) : '-'}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  {formatDateForDisplay(followup.nextFollowupAt)}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  {followup.nextFollowupAt ? new Date(followup.nextFollowupAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '-'}
                                </td>
                                <td style={{ padding: '12px', maxWidth: '420px' }}>
                                  <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {followup.summary}
                                  </div>
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                  <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <button
                                      className="btn btn-secondary"
                                      style={{ fontSize: '11px', padding: '4px 8px' }}
                                      onClick={() => { window.location.href = `/customers/${followup.customerId}`; }}
                                    >
                                      View Customer
                                    </button>
                                    <button
                                      className="btn btn-primary"
                                      style={{ fontSize: '11px', padding: '4px 8px' }}
                                      onClick={() => openFollowupHistory(followup)}
                                    >
                                      Show Details
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : showQuickAction === 'settlements' ? (
                  <div>
                    {quickActionData.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                        No pending product settlements found.
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                          <thead><tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                            <th onClick={() => handleSort('customerName')} style={{ padding: '12px', textAlign: 'left', cursor: 'pointer' }}>Customer {getSortIcon('customerName')}</th>
                            <th onClick={() => handleSort('dueDate')} style={{ padding: '12px', textAlign: 'left', cursor: 'pointer' }}>Due Date {getSortIcon('dueDate')}</th>
                            <th style={{ padding: '12px', textAlign: 'left' }}>Products</th>
                            <th onClick={() => handleSort('productQuantity')} style={{ padding: '12px', textAlign: 'right', cursor: 'pointer' }}>Pending Quantity {getSortIcon('productQuantity')}</th>
                            <th style={{ padding: '12px', textAlign: 'center' }}>Action</th>
                          </tr></thead>
                          <tbody>{quickActionData.map((settlement, index) => <tr key={settlement.id} style={{ backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)', borderBottom: '1px solid var(--gray-medium)' }}>
                            <td style={{ padding: '12px', fontWeight: 600 }}>{settlement.customerName}</td>
                            <td style={{ padding: '12px' }}>{formatDateForDisplay(settlement.dueDate)}</td>
                            <td style={{ padding: '12px' }}>{settlement.items.map(item => <div key={item.id}>{item.productName}: {item.pendingQuantity} pending</div>)}</td>
                            <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700 }}>{settlement.pendingQuantity}</td>
                            <td style={{ padding: '12px', textAlign: 'center' }}><button className="btn btn-primary" style={{ fontSize: '11px', padding: '4px 8px' }} onClick={() => { window.location.href = `/customers/${settlement.customerId}?tab=settlements`; }}>View</button></td>
                          </tr>)}</tbody>
                        </table>
                      </div>
                    )}
                    {totalPages > 1 && <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px', gap: '10px' }}>
                      <button className="btn btn-secondary" disabled={currentPage === 0} onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
                      <span>Page {currentPage + 1} of {totalPages}</span>
                      <button className="btn btn-secondary" disabled={currentPage === totalPages - 1} onClick={() => handlePageChange(currentPage + 1)}>Next</button>
                    </div>}
                  </div>
                ) : showQuickAction === 'payments' ? (
                  // Due Payments Table with Pagination
                  <div>
                    {totalElements > 0 && (
                      <div style={{
                        marginBottom: '15px',
                        fontSize: '14px',
                        color: 'var(--gray-dark)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span>
                          Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} invoices
                        </span>
                        <span>
                          Page {currentPage + 1} of {totalPages}
                        </span>
                      </div>
                    )}

                    {quickActionData.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                        No pending payment invoices found.
                      </div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ 
                          width: '100%', 
                          borderCollapse: 'collapse',
                          fontSize: '14px'
                        }}>
                          <thead>
                            <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                              <th 
                                style={{ 
                                  padding: '12px', 
                                  textAlign: 'left', 
                                  cursor: 'pointer',
                                  borderBottom: '2px solid var(--light-blue)'
                                }}
                                onClick={() => handleSort('invoiceNumber')}
                              >
                                Invoice # {getSortIcon('invoiceNumber')}
                              </th>
                              <th 
                                style={{ 
                                  padding: '12px', 
                                  textAlign: 'left', 
                                  cursor: 'pointer',
                                  borderBottom: '2px solid var(--light-blue)'
                                }}
                                onClick={() => handleSort('customerName')}
                              >
                                Customer {getSortIcon('customerName')}
                              </th>
                              <th 
                                style={{ 
                                  padding: '12px', 
                                  textAlign: 'left', 
                                  cursor: 'pointer',
                                  borderBottom: '2px solid var(--light-blue)'
                                }}
                                onClick={() => handleSort('invoiceDate')}
                              >
                                Invoice Date {getSortIcon('invoiceDate')}
                              </th>
                              <th 
                                style={{ 
                                  padding: '12px', 
                                  textAlign: 'left', 
                                  cursor: 'pointer',
                                  borderBottom: '2px solid var(--light-blue)'
                                }}
                                onClick={() => handleSort('dueDate')}
                              >
                                Due Date {getSortIcon('dueDate')}
                              </th>
                              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                                Payment Info
                              </th>
                              <th
                                style={{
                                  padding: '12px',
                                  textAlign: 'right',
                                  cursor: 'pointer',
                                  borderBottom: '2px solid var(--light-blue)'
                                }}
                                onClick={() => handleSort('dueAmount')}
                              >
                                Due Amount {getSortIcon('dueAmount')}
                              </th>
                              <th 
                                style={{ 
                                  padding: '12px', 
                                  textAlign: 'left', 
                                  cursor: 'pointer',
                                  borderBottom: '2px solid var(--light-blue)'
                                }}
                                onClick={() => handleSort('status')}
                              >
                                Status {getSortIcon('status')}
                              </th>
                              <th style={{ 
                                padding: '12px', 
                                textAlign: 'center',
                                borderBottom: '2px solid var(--light-blue)'
                              }}>
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {quickActionData.map((invoice, index) => (
                              <tr 
                                key={invoice.id}
                                style={{ 
                                  backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                                  borderBottom: '1px solid var(--gray-medium)'
                                }}
                              >
                                <td style={{ padding: '12px', fontWeight: '500' }}>
                                  {invoice.invoiceNumber}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  <div>{invoice.customerName}</div>
                                  <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                                    {invoice.customerEmail}
                                  </div>
                                </td>
                                <td style={{ padding: '12px' }}>
                                  {formatDateForDisplay(invoice.invoiceDate)}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  {invoice.dueDate ? formatDateForDisplay(invoice.dueDate) : '-'}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  <div style={{ fontSize: '12px' }}>
                                    {invoice.payments && invoice.payments.length > 0 ? (
                                      <div>
                                        <div>Total Paid: ₹{invoice.totalPaidAmount || 0}</div>
                                        <div>Balance: ₹{invoice.remainingBalance || ((invoice.totalAmount || invoice.amount) - (invoice.totalPaidAmount || 0))}</div>
                                        <div>Last: {invoice.payments[0]?.paymentMode}</div>
                                      </div>
                                    ) : (
                                      <div>
                                        {invoice.paymentMode && <div>Mode: {invoice.paymentMode}</div>}
                                        {invoice.paymentType && <div>Type: {invoice.paymentType}</div>}
                                        {invoice.paidAmount && <div>Paid: ₹{invoice.paidAmount}</div>}
                                        {!invoice.paymentMode && !invoice.paymentType && !invoice.paidAmount && (
                                          <div style={{ color: 'var(--error)', fontWeight: '500' }}>
                                            Balance: ₹{invoice.totalAmount || invoice.amount}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: 'var(--error)', whiteSpace: 'nowrap' }}>
                                  ₹{Math.max(
                                    0,
                                    Number(
                                      invoice.remainingBalance ??
                                      (Number(invoice.totalAmount ?? invoice.amount ?? 0) - Number(invoice.totalPaidAmount ?? invoice.paidAmount ?? 0))
                                    )
                                  ).toFixed(2)}
                                </td>
                                <td style={{ padding: '12px' }}>
                                  <span style={{
                                    color: getInvoiceStatusColor(invoice.status),
                                    fontWeight: '500'
                                  }}>
                                    {invoice.status}
                                  </span>
                                </td>
                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                  <button 
                                    className="btn btn-primary"
                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                    onClick={() => {
                                      const customerId = invoice.customerId;
                                      if (customerId) {
                                        trackNavigation('Customer Details', `/customers/${customerId}`);
                                        window.location.href = `/customers/${customerId}?tab=invoices`;
                                      } else {
                                        console.error('Customer ID not found in invoice data:', invoice);
                                        alert('Unable to navigate to customer details. Customer ID not found.');
                                      }
                                    }}
                                    title="View Customer Invoices"
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Pagination for Due Payments */}
                    {totalPages > 1 && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginTop: '20px',
                        gap: '10px'
                      }}>
                        <button
                          className="btn btn-secondary"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 0}
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          Previous
                        </button>
                        
                        <span style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
                          Page {currentPage + 1} of {totalPages}
                        </span>
                        
                        <button
                          className="btn btn-secondary"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages - 1}
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  // Customer Lists (Checkup, Birthday, Renewal)
                  <div>
                    {showQuickAction === 'renewal' && totalElements > 0 && (
                      <div style={{
                        marginBottom: '15px',
                        fontSize: '14px',
                        color: 'var(--gray-dark)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <span>
                          Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} customers
                        </span>
                        <span>
                          Page {currentPage + 1} of {totalPages}
                        </span>
                      </div>
                    )}

                    {quickActionData.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                        {showQuickAction === 'checkup' && "No customers have checkup scheduled for today."}
                        {showQuickAction === 'birthday' && "No customers have birthday today."}
                        {showQuickAction === 'renewal' && "No customers have program renewal due in next 7 days."}
                      </div>
                    ) : (
                      <div style={{ overflowX: showQuickAction === 'renewal' ? 'visible' : 'auto' }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: showQuickAction === 'renewal' ? '13px' : '14px',
                          tableLayout: showQuickAction === 'renewal' ? 'fixed' : 'auto'
                        }}>
                          <thead>
                            <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                              <th
                                style={{
                                  padding: showQuickAction === 'renewal' ? '10px 8px' : '12px',
                                  textAlign: 'left',
                                  cursor: showQuickAction === 'renewal' ? 'pointer' : 'default',
                                  borderBottom: '2px solid var(--light-blue)',
                                  width: showQuickAction === 'renewal' ? '27%' : 'auto'
                                }}
                                onClick={showQuickAction === 'renewal' ? () => handleSort('fullName') : undefined}
                              >
                                Customer Name {showQuickAction === 'renewal' && getSortIcon('fullName')}
                              </th>
                              {showQuickAction !== 'renewal' && (
                                <>
                                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                                    Email
                                  </th>
                                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                                    Phone
                                  </th>
                                </>
                              )}
                              {showQuickAction === 'checkup' && (
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                                  Checkup Day
                                </th>
                              )}
                              {showQuickAction === 'birthday' && (
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>
                                  Date of Birth
                                </th>
                              )}
                              {showQuickAction === 'renewal' && (
                                <>
                                  <th style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)', width: '13%' }}>
                                    Type
                                  </th>
                                  <th style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)', width: '16%' }}>
                                    Points
                                  </th>
                                  <th style={{ padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)', width: '13%' }}>
                                    Attendance
                                  </th>
                                  <th
                                    style={{
                                      padding: '10px 8px',
                                      textAlign: 'left',
                                      cursor: 'pointer',
                                      borderBottom: '2px solid var(--light-blue)',
                                      width: '14%'
                                    }}
                                    onClick={() => handleSort('programEndDate')}
                                  >
                                    End Date {getSortIcon('programEndDate')}
                                  </th>
                                </>
                              )}
                              <th style={{
                                padding: showQuickAction === 'renewal' ? '10px 8px' : '12px',
                                textAlign: 'left',
                                borderBottom: '2px solid var(--light-blue)',
                                width: showQuickAction === 'renewal' ? '10%' : 'auto'
                              }}>
                                Status
                              </th>
                              <th style={{
                                padding: showQuickAction === 'renewal' ? '10px 8px' : '12px',
                                textAlign: 'center',
                                borderBottom: '2px solid var(--light-blue)',
                                width: showQuickAction === 'renewal' ? '7%' : 'auto'
                              }}>
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {quickActionData.map((customer, index) => (
                              <tr 
                                key={customer.id}
                                style={{ 
                                  backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                                  borderBottom: '1px solid var(--gray-medium)'
                                }}
                              >
                                <td style={{
                                  padding: showQuickAction === 'renewal' ? '10px 8px' : '12px',
                                  fontWeight: '500',
                                  overflowWrap: 'anywhere'
                                }}>
                                  <div>{customer.fullName || 'N/A'}</div>
                                  {showQuickAction === 'renewal' && (
                                    <div style={{
                                      marginTop: '4px',
                                      fontSize: '11px',
                                      lineHeight: '1.35',
                                      color: 'var(--gray-dark)',
                                      fontWeight: '400'
                                    }}>
                                      <div>{customer.email || 'N/A'}</div>
                                      <div>{customer.phone || 'N/A'}</div>
                                    </div>
                                  )}
                                </td>
                                {showQuickAction !== 'renewal' && (
                                  <>
                                    <td style={{ padding: '12px' }}>
                                      {customer.email}
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                      {customer.phone || 'N/A'}
                                    </td>
                                  </>
                                )}
                                {showQuickAction === 'checkup' && (
                                  <td style={{ padding: '12px' }}>
                                    {customer.checkupDay || 'N/A'}
                                  </td>
                                )}
                                {showQuickAction === 'birthday' && (
                                  <td style={{ padding: '12px' }}>
                                    {customer.dateOfBirth ? formatDateForDisplay(customer.dateOfBirth) : 'N/A'}
                                  </td>
                                )}
                                {showQuickAction === 'renewal' && (
                                  <>
                                    <td style={{ padding: '10px 8px' }}>
                                      <span style={{
                                        backgroundColor: customer.customerType === 'ONLINE' ? 'var(--primary-blue)' : 'var(--success)',
                                        color: 'white',
                                        padding: '4px 7px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: '500'
                                      }}>
                                        {customer.customerType || 'N/A'}
                                      </span>
                                    </td>
                                    <td style={{ padding: '10px 8px' }}>
                                      {customer.volumePointConsumption && customer.totalVolumePoints ? (
                                        <div>
                                          <div style={{ fontWeight: '500' }}>
                                            {customer.volumePointConsumption} / {customer.totalVolumePoints}
                                          </div>
                                          <div style={{ 
                                            fontSize: '11px', 
                                            color: 'var(--gray-dark)',
                                            marginTop: '2px'
                                          }}>
                                            {((customer.volumePointConsumption / customer.totalVolumePoints) * 100).toFixed(1)}% consumed
                                          </div>
                                        </div>
                                      ) : (
                                        <span style={{ color: 'var(--gray-dark)' }}>No data</span>
                                      )}
                                    </td>
                                    <td style={{ padding: '10px 8px' }}>
                                      <div style={{ fontWeight: '500' }}>
                                        {customer.daysAttended || 0}
                                      </div>
                                      <div style={{ fontSize: '11px', color: 'var(--gray-dark)' }}>
                                        Present
                                      </div>
                                    </td>
                                    <td style={{ padding: '10px 8px' }}>
                                      {customer.programEndDate ? formatDateForDisplay(customer.programEndDate) : 'N/A'}
                                    </td>
                                  </>
                                )}
                                <td style={{ padding: showQuickAction === 'renewal' ? '10px 8px' : '12px' }}>
                                  <span style={{
                                    color: getStatusColor(customer.status),
                                    fontWeight: '500'
                                  }}>
                                    {customer.status}
                                  </span>
                                </td>
                                <td style={{ padding: showQuickAction === 'renewal' ? '10px 8px' : '12px', textAlign: 'center' }}>
                                  <button 
                                    className="btn btn-primary"
                                    style={{ fontSize: '11px', padding: '4px 8px' }}
                                    onClick={() => {
                                      trackNavigation('Customer Details', `/customers/${customer.id}`);
                                      window.location.href = `/customers/${customer.id}?tab=programs`;
                                    }}
                                    title="View Customer Details"
                                  >
                                    View
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {showQuickAction === 'renewal' && totalPages > 1 && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginTop: '20px',
                        gap: '10px'
                      }}>
                        <button
                          className="btn btn-secondary"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 0}
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          Previous
                        </button>

                        <span style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
                          Page {currentPage + 1} of {totalPages}
                        </span>

                        <button
                          className="btn btn-secondary"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages - 1}
                          style={{ fontSize: '12px', padding: '6px 12px' }}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showLeadFollowupForm && selectedLeadDetails && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <div>
                <h3>Add Followup</h3>
                <div style={{ color: 'var(--gray-dark)', fontSize: '13px', marginTop: '4px' }}>
                  {selectedLeadDetails.fullName}
                </div>
              </div>
              <button
                type="button"
                className="close-btn"
                onClick={() => setShowLeadFollowupForm(false)}
                disabled={leadFollowupSaving}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <form className="profile-form" onSubmit={submitLeadFollowup}>
              {leadFollowupFormError && <div className="alert alert-error">{leadFollowupFormError}</div>}

              <div className="form-group">
                <label htmlFor="dashboardLeadFollowupDescription" className="form-label">
                  Description / Comment *
                </label>
                <textarea
                  id="dashboardLeadFollowupDescription"
                  className="form-input"
                  rows="4"
                  maxLength="5000"
                  value={leadFollowupForm.description}
                  onChange={(event) => setLeadFollowupForm({ ...leadFollowupForm, description: event.target.value })}
                  required
                  disabled={leadFollowupSaving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="dashboardLeadNextFollowupAt" className="form-label">
                  Next Followup Date & Time *
                </label>
                <input
                  id="dashboardLeadNextFollowupAt"
                  type="datetime-local"
                  className="form-input"
                  value={leadFollowupForm.nextFollowupAt}
                  onChange={(event) => setLeadFollowupForm({ ...leadFollowupForm, nextFollowupAt: event.target.value })}
                  required
                  disabled={leadFollowupSaving}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowLeadFollowupForm(false)}
                  disabled={leadFollowupSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={leadFollowupSaving || !leadFollowupForm.description.trim() || !leadFollowupForm.nextFollowupAt}
                >
                  {leadFollowupSaving ? 'Saving...' : 'Save Followup'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedLeadDetails && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.62)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1200,
          padding: '24px'
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '28px',
            width: '95%',
            maxWidth: '760px',
            maxHeight: '86vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
              <div>
                <h3 style={{ color: 'var(--dark-blue)', margin: '0 0 6px' }}>Lead Details</h3>
                <div style={{ color: 'var(--gray-dark)', fontSize: '14px' }}>
                  {selectedLeadDetails.fullName || 'Lead'}
                </div>
              </div>
              <button
                type="button"
                className="close-btn"
                onClick={() => {
                  setSelectedLeadDetails(null);
                  setLeadDetailsFollowups([]);
                  setLeadDetailsError('');
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {leadDetailsError && <div className="alert alert-error">{leadDetailsError}</div>}

            {leadDetailsLoading ? (
              <div className="loading">Loading lead details...</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px', marginBottom: '22px' }}>
                  <div>
                    <h4 style={{ color: 'var(--primary-blue)', margin: '0 0 12px' }}>Personal Information</h4>
                    <div style={{ marginBottom: '8px' }}><strong>Full Name:</strong> {selectedLeadDetails.fullName || 'N/A'}</div>
                    <div style={{ marginBottom: '8px' }}><strong>Email:</strong> {selectedLeadDetails.email || 'N/A'}</div>
                    <div style={{ marginBottom: '8px' }}><strong>Phone:</strong> {selectedLeadDetails.phone || 'N/A'}</div>
                    <div style={{ marginBottom: '8px' }}><strong>Gender:</strong> {selectedLeadDetails.gender || 'N/A'}</div>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Date of Birth:</strong> {selectedLeadDetails.dateOfBirth ? formatDateForDisplay(selectedLeadDetails.dateOfBirth) : 'N/A'}
                    </div>
                    <div><strong>Age:</strong> {selectedLeadDetails.age || 'N/A'}</div>
                  </div>

                  <div>
                    <h4 style={{ color: 'var(--primary-blue)', margin: '0 0 12px' }}>Lead Information</h4>
                    <div style={{ marginBottom: '8px' }}><strong>Customer Type:</strong> {selectedLeadDetails.customerType || 'N/A'}</div>
                    <div style={{ marginBottom: '8px' }}><strong>Weight:</strong> {selectedLeadDetails.weightKg ? `${selectedLeadDetails.weightKg} kg` : 'N/A'}</div>
                    <div style={{ marginBottom: '8px' }}><strong>Height:</strong> {selectedLeadDetails.heightCm ? `${selectedLeadDetails.heightCm} cm` : 'N/A'}</div>
                    <div style={{ marginBottom: '8px' }}><strong>Location:</strong> {selectedLeadDetails.location || 'N/A'}</div>
                    <div style={{ marginBottom: '8px' }}><strong>Lead Source:</strong> {selectedLeadDetails.leadSource || 'N/A'}</div>
                    <div><strong>Status:</strong> {selectedLeadDetails.status || 'N/A'}</div>
                  </div>
                </div>

                {selectedLeadDetails.comment && (
                  <div style={{ marginBottom: '22px' }}>
                    <h4 style={{ color: 'var(--primary-blue)', margin: '0 0 10px' }}>Comment</h4>
                    <div style={{ padding: '12px', background: 'var(--gray-light)', borderRadius: '6px', whiteSpace: 'pre-wrap' }}>
                      {selectedLeadDetails.comment}
                    </div>
                  </div>
                )}

                <div style={{ borderTop: '1px solid var(--gray-medium)', paddingTop: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                    <h4 style={{ color: 'var(--primary-blue)', margin: 0 }}>Followup History</h4>
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ fontSize: '12px', padding: '6px 10px' }}
                      onClick={openLeadFollowupForm}
                    >
                      Add Followup
                    </button>
                  </div>
                  {leadDetailsFollowups.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px', color: 'var(--gray-dark)', background: 'var(--gray-light)', borderRadius: '6px' }}>
                      No followup history found.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '320px', overflowY: 'auto' }}>
                      {leadDetailsFollowups.map((followup) => (
                        <div
                          key={followup.id}
                          style={{ border: '1px solid var(--gray-medium)', borderRadius: '6px', padding: '14px' }}
                        >
                          <div style={{ whiteSpace: 'pre-wrap', marginBottom: '10px' }}>{followup.description}</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', color: 'var(--gray-dark)', fontSize: '12px' }}>
                            <span><strong>Next Followup:</strong> {new Date(followup.nextFollowupAt).toLocaleString()}</span>
                            <span>Added {new Date(followup.createdAt).toLocaleString()} by {followup.coachName || 'Coach'}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  {selectedLeadDetails.status === 'NEW' && (
                    <>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ color: 'var(--error)' }}
                        onClick={() => updateLeadFromDashboard('close')}
                        disabled={leadActionLoading}
                      >
                        Mark as Closed
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        style={{ background: 'var(--success)', border: 'none' }}
                        onClick={() => updateLeadFromDashboard('convert')}
                        disabled={leadActionLoading}
                      >
                        {leadActionLoading ? 'Processing...' : 'Convert To Customer'}
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setSelectedLeadDetails(null);
                      setLeadDetailsFollowups([]);
                      setLeadDetailsError('');
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {selectedFollowupCustomer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.62)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1200,
          padding: '24px'
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '28px',
            width: '95%',
            maxWidth: '760px',
            maxHeight: '86vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
              <div>
                <h3 style={{ color: 'var(--dark-blue)', margin: '0 0 6px' }}>
                  Followup Details
                </h3>
                <div style={{ color: 'var(--gray-dark)', fontSize: '14px' }}>
                  {selectedFollowupCustomer.customerName}
                  {selectedFollowupCustomer.customerType && (
                    <span style={{ marginLeft: '8px' }}>
                      · {t(selectedFollowupCustomer.customerType)}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => { window.location.href = `/customers/${selectedFollowupCustomer.customerId}`; }}
                >
                  View Customer Profile
                </button>
                <button
                  type="button"
                  className="close-btn"
                  onClick={() => setSelectedFollowupCustomer(null)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>

            {followupHistoryError && <div className="alert alert-error">{followupHistoryError}</div>}

            {followupHistoryLoading ? (
              <div className="loading">Loading followup history...</div>
            ) : followupHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: 'var(--gray-dark)' }}>
                No followup history found.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {followupHistory.map((followup) => (
                  <div
                    key={followup.id}
                    style={{
                      border: '1px solid var(--gray-medium)',
                      borderRadius: '8px',
                      padding: '16px',
                      backgroundColor: 'var(--white)'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '12px',
                      flexWrap: 'wrap',
                      marginBottom: '10px',
                      color: 'var(--gray-dark)',
                      fontSize: '13px'
                    }}>
                      <span>
                        Added: {formatDateForDisplay(followup.createdAt)}
                      </span>
                      <span>
                        Next: {formatDateForDisplay(followup.nextFollowupAt)} {followup.nextFollowupAt ? new Date(followup.nextFollowupAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.5', color: 'var(--dark-blue)' }}>
                      {followup.summary}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setSelectedFollowupCustomer(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showQuickAddModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(7, 26, 54, 0.48)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1100,
          padding: '24px'
        }}>
          <div style={{
            width: 'min(1100px, 100%)',
            maxHeight: '90vh',
            overflowY: 'auto',
            borderRadius: '28px',
            background: 'linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)',
            border: '1px solid rgba(76, 125, 204, 0.14)',
            boxShadow: '0 32px 80px rgba(15, 44, 87, 0.22)'
          }}>
            <div style={{
              padding: '28px 30px 20px',
              borderBottom: '1px solid rgba(76, 125, 204, 0.12)',
              background: 'linear-gradient(135deg, rgba(40, 116, 240, 0.08) 0%, rgba(24, 176, 132, 0.08) 100%)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '20px',
                flexWrap: 'wrap'
              }}>
                <div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '700',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: 'var(--primary-blue)',
                    marginBottom: '8px'
                  }}>
                    Today&apos;s Checkup
                  </div>
                  <h3 style={{
                    margin: 0,
                    color: 'var(--dark-blue)',
                    fontSize: '28px'
                  }}>
                    Quick Add Health Reading
                  </h3>
                  <p style={{
                    margin: '10px 0 0',
                    color: 'var(--gray-dark)',
                    maxWidth: '680px'
                  }}>
                    Search an active customer or affiliated customer, confirm their profile snapshot, and capture the latest health reading without leaving the dashboard.
                  </p>
                </div>

                <button
                  onClick={closeQuickAddModal}
                  style={{
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(76, 125, 204, 0.18)',
                    color: 'var(--gray-dark)',
                    width: '42px',
                    height: '42px',
                    borderRadius: '50%',
                    fontSize: '22px',
                    cursor: 'pointer'
                  }}
                  aria-label="Close quick add modal"
                >
                  ×
                </button>
              </div>
            </div>

            <div style={{
              padding: '28px 30px 32px',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '24px'
            }}>
              <div style={{
                background: 'var(--white)',
                border: '1px solid rgba(76, 125, 204, 0.14)',
                borderRadius: '22px',
                boxShadow: '0 16px 40px rgba(15, 44, 87, 0.08)',
                padding: '22px'
              }}>
                <label htmlFor="quickAddSearch" className="form-label">
                  Search Customer
                </label>
                <input
                  id="quickAddSearch"
                  type="text"
                  className="form-input"
                  value={quickAddSearch}
                  onChange={(event) => setQuickAddSearch(event.target.value)}
                  placeholder="Search by customer name"
                  autoFocus
                />

                <div style={{
                  marginTop: '10px',
                  fontSize: '12px',
                  color: 'var(--gray-dark)'
                }}>
                  Type at least 2 characters to search active direct and affiliated customers.
                </div>

                {quickAddSearchError && (
                  <div className="alert alert-error" style={{ marginTop: '16px' }}>
                    {quickAddSearchError}
                  </div>
                )}

                <div style={{ marginTop: '18px' }}>
                  {quickAddSearchLoading ? (
                    <div className="loading">Searching customers...</div>
                  ) : quickAddSearch.trim().length < 2 ? (
                    <div style={{
                      padding: '18px',
                      borderRadius: '16px',
                      background: 'var(--gray-light)',
                      color: 'var(--gray-dark)',
                      fontSize: '14px'
                    }}>
                      Start typing to load matching customers.
                    </div>
                  ) : quickAddResults.length === 0 ? (
                    <div style={{
                      padding: '18px',
                      borderRadius: '16px',
                      background: 'var(--gray-light)',
                      color: 'var(--gray-dark)',
                      fontSize: '14px'
                    }}>
                      No matching active customers found.
                    </div>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      maxHeight: '420px',
                      overflowY: 'auto',
                      paddingRight: '4px'
                    }}>
                      {quickAddResults.map((customer) => {
                        const isSelected = selectedQuickAddCustomer?.id === customer.id;

                        return (
                          <button
                            key={customer.id}
                            type="button"
                            onClick={() => {
                              setSelectedQuickAddCustomer(customer);
                              setQuickAddSubmitError('');
                            }}
                            style={{
                              textAlign: 'left',
                              border: isSelected ? '2px solid var(--primary-blue)' : '1px solid rgba(76, 125, 204, 0.16)',
                              background: isSelected
                                ? 'linear-gradient(135deg, rgba(40, 116, 240, 0.12) 0%, rgba(24, 176, 132, 0.08) 100%)'
                                : 'var(--white)',
                              borderRadius: '18px',
                              padding: '16px',
                              cursor: 'pointer',
                              boxShadow: isSelected ? '0 14px 30px rgba(40, 116, 240, 0.12)' : 'none'
                            }}
                          >
                            <div style={{
                              fontWeight: '700',
                              color: 'var(--dark-blue)',
                              marginBottom: '6px'
                            }}>
                              {customer.fullName || 'Unnamed Customer'}
                            </div>
                            <div style={{
                              fontSize: '13px',
                              color: 'var(--gray-dark)',
                              marginBottom: '8px'
                            }}>
                              {customer.email || 'No email available'}
                            </div>
                            <div style={{
                              display: 'flex',
                              gap: '8px',
                              flexWrap: 'wrap',
                              fontSize: '12px'
                            }}>
                              <span style={{
                                background: 'rgba(40, 116, 240, 0.1)',
                                color: 'var(--primary-blue)',
                                padding: '4px 8px',
                                borderRadius: '999px',
                                fontWeight: '600'
                              }}>
                                Age: {customer.age ?? 'N/A'}
                              </span>
                              <span style={{
                                background: 'rgba(24, 176, 132, 0.12)',
                                color: 'var(--success)',
                                padding: '4px 8px',
                                borderRadius: '999px',
                                fontWeight: '600'
                              }}>
                                Height: {customer.heightCm ?? 'N/A'} cm
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div style={{
                background: 'var(--white)',
                border: '1px solid rgba(76, 125, 204, 0.14)',
                borderRadius: '22px',
                boxShadow: '0 16px 40px rgba(15, 44, 87, 0.08)',
                padding: '24px'
              }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '14px',
                  marginBottom: '24px'
                }}>
                  <div style={{
                    borderRadius: '18px',
                    padding: '18px',
                    background: 'linear-gradient(135deg, rgba(40, 116, 240, 0.09) 0%, rgba(255, 255, 255, 0.95) 100%)',
                    border: '1px solid rgba(76, 125, 204, 0.14)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginBottom: '6px' }}>
                      Selected Customer
                    </div>
                    <div style={{ fontWeight: '700', color: 'var(--dark-blue)' }}>
                      {selectedQuickAddCustomer?.fullName || 'Choose a customer'}
                    </div>
                  </div>
                  <div style={{
                    borderRadius: '18px',
                    padding: '18px',
                    background: 'linear-gradient(135deg, rgba(24, 176, 132, 0.09) 0%, rgba(255, 255, 255, 0.95) 100%)',
                    border: '1px solid rgba(24, 176, 132, 0.14)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginBottom: '6px' }}>
                      Age
                    </div>
                    <div style={{ fontWeight: '700', color: 'var(--dark-blue)' }}>
                      {selectedQuickAddCustomer?.age ?? 'N/A'}
                    </div>
                  </div>
                  <div style={{
                    borderRadius: '18px',
                    padding: '18px',
                    background: 'linear-gradient(135deg, rgba(244, 179, 80, 0.14) 0%, rgba(255, 255, 255, 0.95) 100%)',
                    border: '1px solid rgba(244, 179, 80, 0.18)'
                  }}>
                    <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginBottom: '6px' }}>
                      Height
                    </div>
                    <div style={{ fontWeight: '700', color: 'var(--dark-blue)' }}>
                      {selectedQuickAddCustomer?.heightCm ?? 'N/A'} cm
                    </div>
                  </div>
                </div>

                {quickAddSubmitError && (
                  <div className="alert alert-error" style={{ marginBottom: '18px' }}>
                    {quickAddSubmitError}
                  </div>
                )}

                {quickAddSuccessMessage && showQuickAddModal && (
                  <div className="alert alert-success" style={{ marginBottom: '18px' }}>
                    {quickAddSuccessMessage}
                  </div>
                )}

                <form onSubmit={handleQuickAddSubmit}>
                  <div className="form-group">
                    <label htmlFor="quickAddReadingDate" className="form-label">
                      Reading Date *
                    </label>
                    <input
                      id="quickAddReadingDate"
                      type="date"
                      className="form-input"
                      value={quickAddReading.readingDate}
                      onChange={(event) => handleQuickAddReadingChange('readingDate', event.target.value)}
                      required
                      disabled={quickAddSubmitting}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '15px' }}>
                    <div className="form-group">
                      <label htmlFor="quickAddWeight" className="form-label">
                        Weight (kg)
                      </label>
                      <input
                        id="quickAddWeight"
                        type="number"
                        step="0.1"
                        className="form-input"
                        value={quickAddReading.weight}
                        onChange={(event) => handleQuickAddReadingChange('weight', event.target.value)}
                        disabled={quickAddSubmitting}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="quickAddTotalFat" className="form-label">
                        Total Fat (%)
                      </label>
                      <input
                        id="quickAddTotalFat"
                        type="number"
                        step="0.1"
                        className="form-input"
                        value={quickAddReading.totalFatPercentage}
                        onChange={(event) => handleQuickAddReadingChange('totalFatPercentage', event.target.value)}
                        disabled={quickAddSubmitting}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="quickAddVisceralFat" className="form-label">
                        Visceral Fat
                      </label>
                      <input
                        id="quickAddVisceralFat"
                        type="number"
                        step="0.1"
                        className="form-input"
                        value={quickAddReading.visceralFat}
                        onChange={(event) => handleQuickAddReadingChange('visceralFat', event.target.value)}
                        disabled={quickAddSubmitting}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="quickAddBmr" className="form-label">
                        BMR
                      </label>
                      <input
                        id="quickAddBmr"
                        type="number"
                        step="0.1"
                        className="form-input"
                        value={quickAddReading.bmr}
                        onChange={(event) => handleQuickAddReadingChange('bmr', event.target.value)}
                        disabled={quickAddSubmitting}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="quickAddBmi" className="form-label">
                        BMI
                      </label>
                      <input
                        id="quickAddBmi"
                        type="number"
                        step="0.1"
                        className="form-input"
                        value={quickAddReading.bmi}
                        onChange={(event) => handleQuickAddReadingChange('bmi', event.target.value)}
                        disabled={quickAddSubmitting}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="quickAddMetaAge" className="form-label">
                        Meta Age
                      </label>
                      <input
                        id="quickAddMetaAge"
                        type="number"
                        className="form-input"
                        value={quickAddReading.metaAge}
                        onChange={(event) => handleQuickAddReadingChange('metaAge', event.target.value)}
                        disabled={quickAddSubmitting}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="quickAddTsf" className="form-label">
                        TSF
                      </label>
                      <input
                        id="quickAddTsf"
                        type="number"
                        step="0.1"
                        className="form-input"
                        value={quickAddReading.tsf}
                        onChange={(event) => handleQuickAddReadingChange('tsf', event.target.value)}
                        disabled={quickAddSubmitting}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="quickAddSkeletalMuscle" className="form-label">
                        Skeletal Muscle (%)
                      </label>
                      <input
                        id="quickAddSkeletalMuscle"
                        type="number"
                        step="0.1"
                        className="form-input"
                        value={quickAddReading.skeletalMusclePercentage}
                        onChange={(event) => handleQuickAddReadingChange('skeletalMusclePercentage', event.target.value)}
                        disabled={quickAddSubmitting}
                      />
                    </div>
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '12px',
                    marginTop: '24px',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => submitQuickAddReading('addMore')}
                      disabled={quickAddSubmitting || !selectedQuickAddCustomer}
                      style={{ minWidth: '180px' }}
                    >
                      {quickAddSubmitting ? 'Saving...' : 'Save and Add More'}
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={quickAddSubmitting || !selectedQuickAddCustomer}
                      style={{
                        minWidth: '180px',
                        boxShadow: '0 12px 24px rgba(40, 116, 240, 0.18)'
                      }}
                    >
                      {quickAddSubmitting ? 'Saving...' : 'Save and Close'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;
